import { z } from 'zod';

import { EntityId, Page, PageQuery } from '../primitives';
import { SoftDelete, TextFilter, Timestamps } from './common';
import { PublishStatus } from './playlists';

export const HeroType = z.enum(['song', 'playlist']);
export type HeroType = z.infer<typeof HeroType>;

/**
 * `heroes` row for the console (docs/06 §2.6, Figma CMS-07). Poster paths are nullable: a hero
 * is created from name + target and the two posters are uploaded afterwards (the upload key
 * needs the hero id). Publishing requires both — see `HERO_PUBLISH_REQUIREMENTS`.
 */
export const CmsHeroDTO = z
  .object({
    id: EntityId,
    name: z.string(),
    type: HeroType,
    refId: EntityId,
    /** Title of the target song/playlist, `''` when it no longer exists. */
    refTitle: z.string(),
    /** Artist line of the target song; `''` for a playlist target. */
    refSubtitle: z.string(),
    /** Songs in the target playlist; null for a song target (the client localizes the label). */
    refCount: z.number().int().nonnegative().nullable(),
    refImageUrl: z.string().nullable(),
    /** True when the target is published — an unpublished target never reaches `GET /home`. */
    refPublished: z.boolean(),
    posterTvPath: z.string().nullable(),
    posterTvUrl: z.string().nullable(),
    posterMobilePath: z.string().nullable(),
    posterMobileUrl: z.string().nullable(),
    status: PublishStatus,
  })
  .merge(Timestamps)
  .merge(SoftDelete);
export type CmsHeroDTO = z.infer<typeof CmsHeroDTO>;

/** `GET /cms/heroes` */
export const CmsHeroesQuery = PageQuery.extend({
  q: TextFilter.optional(),
  status: PublishStatus.optional(),
  type: HeroType.optional(),
});
export type CmsHeroesQuery = z.infer<typeof CmsHeroesQuery>;
export type CmsHeroesQueryInput = z.input<typeof CmsHeroesQuery>;

export const CmsHeroesResponse = Page(CmsHeroDTO);
export type CmsHeroesResponse = z.infer<typeof CmsHeroesResponse>;

/** `POST /cms/heroes` */
export const CmsHeroCreateBody = z.object({
  name: z.string().trim().min(1).max(120),
  type: HeroType,
  refId: EntityId,
  posterTvPath: z.string().max(512).nullable().optional(),
  posterMobilePath: z.string().max(512).nullable().optional(),
  status: PublishStatus.default('draft'),
});
export type CmsHeroCreateBody = z.infer<typeof CmsHeroCreateBody>;
export type CmsHeroCreateBodyInput = z.input<typeof CmsHeroCreateBody>;

/** `PATCH /cms/heroes/:id` — at least one key, unknown keys refused. */
export const CmsHeroPatchBody = CmsHeroCreateBody.partial()
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Empty patch' });
export type CmsHeroPatchBody = z.infer<typeof CmsHeroPatchBody>;

export const CmsHeroResponse = z.object({ hero: CmsHeroDTO });
export type CmsHeroResponse = z.infer<typeof CmsHeroResponse>;

/** Poster sizes (docs/06 §2.6). */
export const HERO_POSTER_TV = { width: 1920, height: 720 } as const;
export const HERO_POSTER_MOBILE = { width: 1080, height: 1350 } as const;

/** What `409 conflict { missing }` lists when a hero cannot be published yet. */
export const HERO_PUBLISH_REQUIREMENTS = ['posterTv', 'posterMobile', 'target'] as const;
export type HeroPublishRequirement = (typeof HERO_PUBLISH_REQUIREMENTS)[number];
